import PropTypes from 'prop-types';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import useAuth from '../hooks/useAuth';
import { getPendingClientLandingPath } from '../pages/companies/create-client-wizard/pendingClientSubscription';
import {
  getAuthenticatedLandingPath,
  ONBOARDING_CREATE_PATH,
  ONBOARDING_FEATURE_ENABLED,
} from '../utils/permissionLanding';

interface AuthGuardProps {
  children: ReactNode;
}

const AuthGuard: FC<AuthGuardProps> = (props) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);
  const is2FAEnabled = localStorage.getItem('is2FAEnabled') === 'true';
  const isOtpVerified = localStorage.getItem('isOtpVerified') === 'true';

  const isOnBoarded = auth?.user?.organization?.isOnBoarded;
  const isOnboardingRoute = location.pathname === ONBOARDING_CREATE_PATH;

  // Capture deep-link when bounced to login (never setState during render).
  useEffect(() => {
    if (!auth?.isInitialized || auth?.isAuthenticated) return;
    setRequestedLocation((prev) =>
      prev === location.pathname ? prev : location.pathname
    );
  }, [auth?.isInitialized, auth?.isAuthenticated, location.pathname]);

  // Clear pending restore when forcing onboarding.
  useEffect(() => {
    if (
      ONBOARDING_FEATURE_ENABLED &&
      isOnBoarded === false &&
      !isOnboardingRoute &&
      requestedLocation
    ) {
      setRequestedLocation(null);
    }
  }, [isOnBoarded, isOnboardingRoute, requestedLocation]);

  // Clear after the restore Navigate has landed on the requested path.
  useEffect(() => {
    if (
      auth?.isAuthenticated &&
      requestedLocation &&
      location.pathname === requestedLocation
    ) {
      setRequestedLocation(null);
    }
  }, [auth?.isAuthenticated, requestedLocation, location.pathname]);

  // Clear 2FA / temp login keys once fully authenticated past the 2FA gate.
  useEffect(() => {
    if (!auth?.isAuthenticated) return;
    if (is2FAEnabled && !isOtpVerified) return;
    localStorage.removeItem('_te');
    localStorage.removeItem('is2FAEnabled');
    localStorage.removeItem('isOtpVerified');
    localStorage.removeItem('isQrVerified');
    localStorage.removeItem('_tp');
  }, [auth?.isAuthenticated, is2FAEnabled, isOtpVerified]);

  if (is2FAEnabled && !isOtpVerified) {
    return <Navigate to="/login/two-factor-authentication" />;
  }

  if (!auth?.isInitialized) {
    return null;
  }

  if (!auth?.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (ONBOARDING_FEATURE_ENABLED && isOnBoarded === false && !isOnboardingRoute) {
    return <Navigate to={ONBOARDING_CREATE_PATH} replace />;
  }

  if (
    isOnboardingRoute &&
    (!ONBOARDING_FEATURE_ENABLED || isOnBoarded === true)
  ) {
    return (
      <Navigate to={getAuthenticatedLandingPath(auth?.user)} replace />
    );
  }

  if (
    (!ONBOARDING_FEATURE_ENABLED || isOnBoarded !== false) &&
    requestedLocation &&
    location.pathname !== requestedLocation
  ) {
    const pendingPath = getPendingClientLandingPath();
    if (pendingPath) {
      return <Navigate to={pendingPath} replace />;
    }
    if (requestedLocation.startsWith('/projects')) {
      return <Navigate to={getAuthenticatedLandingPath(auth?.user)} />;
    }
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
};

export default AuthGuard;
